import { useSetAtom } from 'jotai'
import { match } from 'ts-pattern'
import {
  Text,
  BorderRadius4,
  Button,
  GreyGrey70,
  GreyGrey90,
  IconCheckmark,
  IconEmail,
  IconFlag,
  Link,
  Primary13Regular,
  Primary14SemiBold,
  Center,
  Box,
  Flex
} from '@sequencehq/core-components'
import {
  InvoicePreviewCard,
  InvoicePreviewCardSkeleton,
  NodeTimeline
} from 'Automations/components'
import {
  CancelledItem,
  TerminalStateItem
} from 'Automations/nodeDefinitions/executionSummaryUtils'
import { InboxPreview, MenuManualTaskActions } from 'Automations/components'
import { manualTaskProcessorTaskIdAtom } from 'Automations/watchtower/atoms'
import { useManualTask } from 'Automations/watchtower/inbox/manualTasks/hooks/useManualTask'
import { SourceEmailPreview } from 'Automations/watchtower/inbox/manualTasks/components'
import { useInvoiceData } from 'Automations/watchtower/inbox/notifications/hooks/useInvoiceData'
import {
  InvoiceUpdateReviewInput,
  InvoiceUpdateReviewOutcome
} from '../../types'
import {
  getInvoiceLabel,
  getInvoiceReviewTitle,
  mapInputToViewModel
} from '../../invoiceUpdateReview.helpers'
import { useDismissInvoiceUpdateReviewTask } from '../../hooks/useDismissInvoiceUpdateReviewTask'
import { InvoiceReviewCustomerLogo } from '../InvoiceReviewCustomerLogo'

/**
 * Right-hand "details / logs" pane for an invoice-update review task: the agent's
 * summary of what it proposed, with the invoice shown as the shared invoice card,
 * plus the primary "Review change" action that opens the full-screen review modal.
 * Mirrors the customer-info review preview, swapping the customer/contact cards
 * for the invoice card.
 */
export const InvoiceUpdateReviewPreview = ({ taskId }: { taskId: string }) => {
  const { manualTask } = useManualTask({ taskId })
  const setProcessorTaskId = useSetAtom(manualTaskProcessorTaskIdAtom)

  const input = manualTask?.input as InvoiceUpdateReviewInput | undefined
  const { invoice, customer, invoiceDetails } = useInvoiceData(input?.invoiceId)

  if (!input) {
    return <InboxPreview.EmptyState />
  }

  const {
    invoiceId,
    summary,
    source,
    invoice: invoiceDisplay
  } = mapInputToViewModel(input)
  const title = getInvoiceReviewTitle(invoiceDisplay)
  const invoiceLabel = getInvoiceLabel(invoiceDisplay)

  // Once the task is resolved (confirmed or dismissed) there's nothing left to
  // action, so the review/dismiss/cancel controls are replaced by a passive link
  // through to the invoice — mirroring the customer-info review.
  const isPending = manualTask?.status === 'PENDING'

  // Terminal timeline step: once resolved, the timeline is capped with the
  // outcome (applied / dismissed / cancelled), reusing the shared terminal-state
  // items. The outcome is recorded under the task's `output` on completion; a
  // `CANCELLED` status has no outcome (the task was cancelled externally).
  const outcome = manualTask?.output?.outcome as
    | InvoiceUpdateReviewOutcome['outcome']
    | undefined
  const resolvedStep = match({ status: manualTask?.status, outcome })
    .with({ outcome: 'CONFIRMED' }, () => (
      <TerminalStateItem
        icon={<IconCheckmark />}
        label="Invoice updated"
        timestamp={manualTask?.updatedAt}
        isLatestNodeExecution
      />
    ))
    .with({ outcome: 'DISMISSED' }, () => (
      <CancelledItem
        label="Changes dismissed"
        timestamp={manualTask?.updatedAt}
        isLatestNodeExecution
      />
    ))
    .with({ status: 'CANCELLED' }, () => (
      <CancelledItem
        label="Review cancelled"
        timestamp={manualTask?.updatedAt}
        isLatestNodeExecution
      />
    ))
    .otherwise(() => null)

  const hasInvoiceData = invoice && customer && invoiceDetails

  return (
    <InboxPreview
      header={
        <PreviewHeader
          customerId={invoiceDisplay.customerId}
          title={`${invoiceDisplay.customerLegalCompanyName}: ${title}`}
        />
      }
      actions={
        isPending ? (
          <>
            <DismissButton taskId={taskId} invoiceLabel={invoiceLabel} />
            <Button
              variant="primary"
              onClick={() => setProcessorTaskId(taskId)}
              data-testid="watchtower.invoiceUpdateReview.reviewButton"
            >
              Review change
            </Button>
          </>
        ) : (
          <Button
            as={Link.Unstyled}
            to={`/invoices/${invoiceId}`}
            variant="secondary"
            data-testid="watchtower.invoiceUpdateReview.viewInvoiceButton"
          >
            View invoice
          </Button>
        )
      }
      headerActions={
        isPending ? <MenuManualTaskActions taskId={taskId} /> : undefined
      }
    >
      <NodeTimeline data-testid="watchtower.invoiceUpdateReview.preview">
        {source && (
          <NodeTimeline.Item icon={<IconEmail />} showStem>
            <NodeTimeline.Title>
              <NodeTimeline.UserAction
                label="Email forwarded"
                timestamp={source.receivedAt}
              />
            </NodeTimeline.Title>
            <Box marginBottom="16px">
              <SourceEmailPreview
                source={source}
                data-testid="watchtower.invoiceUpdateReview.preview.emailCard"
              />
            </Box>
          </NodeTimeline.Item>
        )}

        <NodeTimeline.Item icon={<IconFlag />} showStem={Boolean(resolvedStep)}>
          <NodeTimeline.Title>
            <NodeTimeline.UserAction
              label="Invoice change flagged for review"
              timestamp={manualTask?.createdAt}
            />
          </NodeTimeline.Title>

          <Flex flexDirection="column" gap="12px">
            <Text
              {...Primary13Regular}
              color={GreyGrey70}
              whiteSpace="pre-wrap"
            >
              {summary}
            </Text>
            {hasInvoiceData ? (
              <Box data-testid="watchtower.invoiceUpdateReview.preview.invoiceCard">
                <InvoicePreviewCard
                  invoice={invoice}
                  customer={customer}
                  invoiceDetails={invoiceDetails}
                />
              </Box>
            ) : (
              <InvoicePreviewCardSkeleton />
            )}
          </Flex>
        </NodeTimeline.Item>

        {resolvedStep}
      </NodeTimeline>
    </InboxPreview>
  )
}

/**
 * Header action that dismisses the task (the `DISMISSED` outcome — nothing is
 * applied and the task leaves the inbox). Loading state is driven by the
 * mutation's `isPending` (no local state).
 */
const DismissButton = ({
  taskId,
  invoiceLabel
}: {
  taskId: string
  invoiceLabel: string
}) => {
  const { dismiss, isPending } = useDismissInvoiceUpdateReviewTask({
    taskId,
    invoiceLabel
  })

  return (
    <Button
      variant="secondary"
      onClick={dismiss}
      submitting={isPending}
      data-testid="watchtower.invoiceUpdateReview.dismissButton"
    >
      Dismiss
    </Button>
  )
}

const PreviewHeader = ({
  customerId,
  title
}: {
  customerId: string
  title: string
}) => (
  <Flex alignItems="center" gap="8px">
    <Center borderRadius={BorderRadius4} overflow="hidden" boxSize="24px">
      <InvoiceReviewCustomerLogo
        customerId={customerId}
        data-testid="watchtower.invoiceUpdateReview.preview.logo"
      />
    </Center>
    <Text {...Primary14SemiBold} color={GreyGrey90}>
      {title}
    </Text>
  </Flex>
)
